import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import FormModal from '../../components/ui/FormModal';
import { AuthContext } from '../../context/auth/AuthContext';
import { scheduleExamples } from '../../utils/companyType';
import { loadProducts, ProductOption } from './modifiers';
import {
    DAYS, describeSchedule, emptySchedule, loadSchedules, PriceSchedule, saveSchedule, schedulePrice, setScheduleStatus, validateSchedule,
} from './priceSchedules';

const money = (value: number | null | undefined) =>
    Number(value ?? 0).toLocaleString('es-CR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Promociones por horario (happy hour, martes de frutas): precios que se activan solos por día y hora
export const PriceSchedulesPage = () => {
    const navigate = useNavigate();
    const { user } = useContext(AuthContext);
    const examples = scheduleExamples(user?.merchant);

    const [schedules, setSchedules] = useState<PriceSchedule[]>([]);
    const [products, setProducts] = useState<ProductOption[]>([]);
    const [serverTime, setServerTime] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');

    const [editing, setEditing] = useState<PriceSchedule | null>(null);
    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState('');
    const [productSearch, setProductSearch] = useState('');
    const [toDelete, setToDelete] = useState<PriceSchedule | null>(null);

    const productById = useMemo(() => new Map(products.map(p => [p.id, p])), [products]);

    const refresh = async () => {
        setLoading(true);
        setError('');
        try {
            const [data, list] = await Promise.all([loadSchedules(), loadProducts()]);
            setSchedules(data.schedules);
            setServerTime(data.serverTime);
            setProducts(list);
        } catch (e: any) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { refresh(); }, []);

    const update = (changes: Partial<PriceSchedule>) => setEditing(s => s ? { ...s, ...changes } : s);

    const toggleDay = (day: number) => setEditing(s => s ? {
        ...s, days: s.days.includes(day) ? s.days.filter(d => d !== day) : [...s.days, day].sort((a, b) => a - b),
    } : s);

    const toggleProduct = (id: number) => setEditing(s => s ? {
        ...s,
        items: s.items.some(i => i.productId === id)
            ? s.items.filter(i => i.productId !== id)
            : [...s.items, { productId: id, price: null }],
    } : s);

    const setItemPrice = (id: number, price: string) => setEditing(s => s ? {
        ...s, items: s.items.map(i => i.productId === id ? { ...i, price: price === '' ? null : Number(price) } : i),
    } : s);

    const openEditor = (schedule?: PriceSchedule) => {
        setEditing(schedule ? { ...schedule, items: schedule.items.map(i => ({ ...i })) } : emptySchedule());
        setFormError('');
        setProductSearch('');
    };

    const handleSave = async () => {
        if (!editing) return;
        const invalid = validateSchedule(editing);
        if (invalid) {
            setFormError(invalid);
            return;
        }
        setSaving(true);
        setFormError('');
        try {
            await saveSchedule({ ...editing, name: editing.name.trim() });
            setNotice(editing.id ? 'Horario actualizado.' : 'Horario creado.');
            setEditing(null);
            await refresh();
        } catch (e: any) {
            setFormError(e.message);
        } finally {
            setSaving(false);
        }
    };

    const changeStatus = async (schedule: PriceSchedule, status: 'ACT' | 'INA' | 'DEL') => {
        try {
            await setScheduleStatus(schedule.id!, status);
            setNotice(status === 'DEL' ? 'Horario eliminado.' : status === 'ACT' ? 'Horario activado.' : 'Horario pausado.');
            setToDelete(null);
            await refresh();
        } catch (e: any) {
            setError(e.message);
        }
    };

    const filteredProducts = useMemo(() => {
        const term = productSearch.trim().toLowerCase();
        return products.filter(p => !term || p.name.toLowerCase().includes(term) || p.menu.toLowerCase().includes(term));
    }, [products, productSearch]);

    // =============================
    // Editor
    // =============================
    const editor = editing && (
        <FormModal
            title={editing.id ? 'Editar horario' : 'Nuevo horario'}
            subtitle="El precio cambia solo en la app y en el menú QR durante el horario."
            onClose={() => setEditing(null)}
            onSave={handleSave}
            saving={saving}
            error={formError}
            size="lg"
        >
            <div className="row g-3 mb-3">
                <div className="col-12 col-md-6">
                    <label className="form-label small fw-semibold">Nombre</label>
                    <input className="form-control" maxLength={80} placeholder={`Ej.: ${examples.name}`} autoFocus
                        value={editing.name} onChange={e => update({ name: e.target.value })} />
                    <div className="form-text">Sale como motivo del descuento en la factura.</div>
                </div>
                <div className="col-12 col-md-6">
                    <label className="form-label small fw-semibold">Tipo</label>
                    <div className="d-flex gap-3">
                        <div className="form-check">
                            <input className="form-check-input" type="radio" id="typePct" checked={editing.type === 'PCT'} onChange={() => update({ type: 'PCT' })} />
                            <label className="form-check-label" htmlFor="typePct">Descuento %</label>
                        </div>
                        <div className="form-check">
                            <input className="form-check-input" type="radio" id="typePrice" checked={editing.type === 'PRICE'} onChange={() => update({ type: 'PRICE' })} />
                            <label className="form-check-label" htmlFor="typePrice">Precio fijo</label>
                        </div>
                    </div>
                    {editing.type === 'PCT' && (
                        <div className="input-group mt-2" style={{ maxWidth: 160 }}>
                            <input type="number" min={1} max={99} className="form-control" value={editing.value}
                                onChange={e => update({ value: Number(e.target.value) })} />
                            <span className="input-group-text">%</span>
                        </div>
                    )}
                </div>

                <div className="col-12">
                    <label className="form-label small fw-semibold d-block">Días</label>
                    <div className="btn-group flex-wrap">
                        {DAYS.map(d => (
                            <button key={d.value} type="button" title={d.name}
                                className={`btn btn-sm ${editing.days.includes(d.value) ? 'btn-primary' : 'btn-outline-secondary'}`}
                                onClick={() => toggleDay(d.value)}>
                                {d.name.slice(0, 3)}
                            </button>
                        ))}
                    </div>
                </div>
                <div className="col-6 col-md-3">
                    <label className="form-label small fw-semibold">Desde</label>
                    <input type="time" className="form-control" value={editing.start} onChange={e => update({ start: e.target.value })} />
                </div>
                <div className="col-6 col-md-3">
                    <label className="form-label small fw-semibold">Hasta</label>
                    <input type="time" className="form-control" value={editing.end} onChange={e => update({ end: e.target.value })} />
                </div>
                <div className="col-6 col-md-3">
                    <label className="form-label small fw-semibold">Fecha inicio <span className="text-muted fw-normal">(opcional)</span></label>
                    <input type="date" className="form-control" value={editing.from ?? ''} onChange={e => update({ from: e.target.value || null })} />
                </div>
                <div className="col-6 col-md-3">
                    <label className="form-label small fw-semibold">Fecha fin <span className="text-muted fw-normal">(opcional)</span></label>
                    <input type="date" className="form-control" value={editing.to ?? ''} onChange={e => update({ to: e.target.value || null })} />
                </div>
                <div className="col-12 small text-muted">
                    <i className="bi bi-clock me-1" />{describeSchedule(editing)}. Si la hora de fin es menor que la de inicio, termina al día siguiente.
                </div>
            </div>

            <h6 className="mb-1">Productos <span className="badge bg-light text-dark border ms-1">{editing.items.length}</span></h6>
            <input className="form-control form-control-sm mb-2" placeholder="Buscar producto o menú"
                value={productSearch} onChange={e => setProductSearch(e.target.value)} />
            <div className="border rounded" style={{ maxHeight: 280, overflowY: 'auto' }}>
                {filteredProducts.length === 0 && <div className="small text-muted p-2">No hay productos que coincidan.</div>}
                {filteredProducts.map(p => {
                    const item = editing.items.find(i => i.productId === p.id);
                    return (
                        <div key={p.id} className="d-flex align-items-center gap-2 px-2 py-1 border-bottom small">
                            <input type="checkbox" className="form-check-input mt-0" id={`p-${p.id}`} checked={!!item} onChange={() => toggleProduct(p.id)} />
                            <label htmlFor={`p-${p.id}`} className="flex-grow-1 mb-0" style={{ cursor: 'pointer' }}>
                                {p.name} <span className="text-muted">· {p.menu}</span>
                            </label>
                            <span className="text-muted text-nowrap">{money(p.price)}</span>
                            {item && editing.type === 'PRICE' && (
                                <input type="number" min={0} step="0.01" className="form-control form-control-sm" style={{ width: 110 }}
                                    placeholder="Precio" value={item.price ?? ''} onChange={e => setItemPrice(p.id, e.target.value)} />
                            )}
                            {item && editing.type === 'PCT' && (
                                <span className="text-success text-nowrap fw-semibold">{money(schedulePrice(editing, item, p.price))}</span>
                            )}
                        </div>
                    );
                })}
            </div>
        </FormModal>
    );

    // =============================
    // Render
    // =============================
    return (
        <div className="mt-5 container-fluid px-4" style={{ flex: 1, marginTop: 10 }}>
            <div className="d-flex flex-wrap justify-content-between align-items-start mb-3 gap-2">
                <div>
                    <button type="button" className="btn btn-link text-decoration-none px-0 mb-1" onClick={() => navigate('/products')}>
                        <i className="bi bi-arrow-left me-1" /> Productos y menús
                    </button>
                    <h3 className="mb-1">Promociones por horario</h3>
                    <p className="text-muted mb-0">
                        {examples.description}: el precio cambia solo según el día y la hora.
                        {serverTime && <span className="ms-1">Hora del comercio: {serverTime.slice(11)}.</span>}
                    </p>
                </div>
                <button type="button" className="btn btn-primary" onClick={() => openEditor()} disabled={loading}>
                    <i className="bi bi-plus-lg me-1" /> Nuevo horario
                </button>
            </div>

            {error && (
                <div className="alert alert-danger d-flex align-items-start gap-2 py-2">
                    <i className="bi bi-exclamation-circle mt-1" />
                    <div className="small">{error}</div>
                </div>
            )}
            {notice && (
                <div className="alert alert-success d-flex align-items-center gap-2 py-2">
                    <i className="bi bi-check-circle" />
                    <div className="small flex-grow-1">{notice}</div>
                    <button type="button" className="btn-close btn-sm" onClick={() => setNotice('')} />
                </div>
            )}

            {loading ? (
                <div className="text-center text-muted py-5"><span className="spinner-border spinner-border-sm me-2" />Cargando...</div>
            ) : schedules.length === 0 ? (
                <div className="card border-0 shadow-sm">
                    <div className="card-body text-center py-5">
                        <i className="bi bi-clock-history fs-1 text-muted" />
                        <h5 className="mt-2">Todavía no hay promociones por horario</h5>
                        <p className="text-muted">Por ejemplo, {examples.example}.</p>
                        <button type="button" className="btn btn-primary" onClick={() => openEditor()}>
                            <i className="bi bi-plus-lg me-1" /> Nuevo horario
                        </button>
                    </div>
                </div>
            ) : (
                <div className="row g-3">
                    {schedules.map(s => (
                        <div key={s.id} className="col-12 col-md-6 col-xl-4">
                            <div className={`card border-0 shadow-sm h-100 ${s.status === 'INA' ? 'opacity-75' : ''}`}>
                                <div className="card-body d-flex flex-column">
                                    <div className="d-flex justify-content-between align-items-start gap-2">
                                        <div>
                                            <h6 className="mb-1">{s.name}</h6>
                                            {s.status === 'INA'
                                                ? <span className="badge bg-light text-muted border">Pausado</span>
                                                : s.activeNow
                                                    ? <span className="badge bg-success">Aplicando ahora</span>
                                                    : <span className="badge bg-light text-dark border">Activo</span>}
                                            <span className="badge bg-light text-dark border ms-1">
                                                {s.type === 'PCT' ? `${s.value} % de descuento` : 'Precio fijo'}
                                            </span>
                                        </div>
                                        <div className="btn-group btn-group-sm">
                                            <button type="button" className="btn btn-outline-secondary" title={s.status === 'INA' ? 'Activar' : 'Pausar'}
                                                onClick={() => changeStatus(s, s.status === 'INA' ? 'ACT' : 'INA')}>
                                                <i className={`bi ${s.status === 'INA' ? 'bi-play' : 'bi-pause'}`} />
                                            </button>
                                            <button type="button" className="btn btn-outline-secondary" title="Editar" onClick={() => openEditor(s)}>
                                                <i className="bi bi-pencil" />
                                            </button>
                                            <button type="button" className="btn btn-outline-danger" title="Eliminar" onClick={() => setToDelete(s)}>
                                                <i className="bi bi-trash" />
                                            </button>
                                        </div>
                                    </div>
                                    <div className="small text-muted my-2"><i className="bi bi-clock me-1" />{describeSchedule(s)}</div>
                                    <ul className="list-unstyled small mb-0 flex-grow-1">
                                        {s.items.slice(0, 6).map(item => {
                                            const product = productById.get(item.productId);
                                            return (
                                                <li key={item.productId} className="d-flex justify-content-between border-bottom py-1">
                                                    <span>{product?.name ?? `Producto #${item.productId}`}</span>
                                                    <span>
                                                        {product && <span className="text-muted text-decoration-line-through me-2">{money(product.price)}</span>}
                                                        <span className="fw-semibold">{money(product ? schedulePrice(s, item, product.price) : item.price)}</span>
                                                    </span>
                                                </li>
                                            );
                                        })}
                                        {s.items.length > 6 && <li className="text-muted py-1">y {s.items.length - 6} más</li>}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {editor}

            {toDelete && (
                <FormModal title="Eliminar horario" onClose={() => setToDelete(null)} onSave={() => changeStatus(toDelete, 'DEL')} saveLabel="Eliminar" size="sm">
                    <p className="mb-0">¿Eliminar <strong>{toDelete.name}</strong>? Los precios vuelven al de catálogo. Las órdenes ya hechas no cambian.</p>
                </FormModal>
            )}
        </div>
    );
};
